import { DeviceOperationalState } from '@/__generated__/graphql';
import { getDeviceOperationalStateAttributes } from '@/utils/attributes/deviceOperationalState';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import ModalActions from '@components/modal/ModalActions';
import { Access, useSuspenseAuthorization } from '@features/authorization';
import { useUpdateDeviceOperationalStateForm } from '@features/deviceConfigurationModal/hooks/useUpdateDeviceStatusForm';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ArrowRightAlt } from '@mui/icons-material';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { Suspense, useCallback, useMemo, useState, type FC } from 'react';
import { Controller } from 'react-hook-form';
import { TabContent, TabFallback } from '../common';
import { DeviceTabProps } from '../tabProps';
import { AfterUpdateTasks } from './AfterUpdateTasks';
import { useConfetti } from '@/hooks/useConfetti';

const Component: FC<DeviceTabProps> = (props) => {
  const [openedAfterUpdateTasks, setOpenedAfterUpdateTasks] = useState(false);
  const { partyHard } = useConfetti();
  const onUpdateCompleted = useCallback(
    (previousState: DeviceOperationalState, newState: DeviceOperationalState) => {
      if (
        previousState === DeviceOperationalState.Initialization &&
        newState === DeviceOperationalState.Active
      ) {
        partyHard();
      }

      if (
        previousState === DeviceOperationalState.Maintenance &&
        newState === DeviceOperationalState.Active
      ) {
        setOpenedAfterUpdateTasks(true);
      }
    },
    [setOpenedAfterUpdateTasks, partyHard],
  );
  const {
    currentOperationalState,
    methods: { formState, control, reset },
    onSubmit,
  } = useUpdateDeviceOperationalStateForm({
    ...props,
    onUpdateCompleted,
  });
  const { _, i18n } = useLingui();
  const { validateAccess } = useSuspenseAuthorization(props.deviceId);

  const { to } = useMemo(() => {
    const edges = (
      [
        [
          validateAccess(Access.UpdateDeviceOperationalState_DevelopmentToInitialization),
          DeviceOperationalState.Development,
          DeviceOperationalState.Initialization,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_DevelopmentToOutOfService),
          DeviceOperationalState.Development,
          DeviceOperationalState.OutOfService,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_InitializationToActive),
          DeviceOperationalState.Initialization,
          DeviceOperationalState.Active,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_InitializationToOutOfService),
          DeviceOperationalState.Initialization,
          DeviceOperationalState.OutOfService,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_ActiveToMaintenance),
          DeviceOperationalState.Active,
          DeviceOperationalState.Maintenance,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_ActiveToOutOfService),
          DeviceOperationalState.Active,
          DeviceOperationalState.OutOfService,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_MaintenanceToActive),
          DeviceOperationalState.Maintenance,
          DeviceOperationalState.Active,
        ],
        [
          validateAccess(Access.UpdateDeviceOperationalState_MaintenanceToOutOfService),
          DeviceOperationalState.Maintenance,
          DeviceOperationalState.OutOfService,
        ],
      ] as const
    )
      .filter(([valid]) => valid)
      .map((row) => ({ from: row[1], to: row[2] }));

    return {
      to(fromState: DeviceOperationalState) {
        return edges.filter(({ from }) => from === fromState).map(({ to }) => to);
      },
    };
  }, [validateAccess]);

  return (
    <>
      <AfterUpdateTasks
        deviceId={props.deviceId}
        open={openedAfterUpdateTasks}
        onClose={() => setOpenedAfterUpdateTasks(false)}
      />
      <form onSubmit={onSubmit}>
        <TabContent>
          <Typography variant="h6" mb={2}>
            <Trans>Cambiar estado</Trans>
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr auto 1fr" alignItems="center" gap={2}>
            <Autocomplete
              disabled
              value={currentOperationalState}
              options={[currentOperationalState]}
              getOptionLabel={(option) => {
                const { label } = getDeviceOperationalStateAttributes(option, i18n);
                return label;
              }}
              renderInput={(props) => {
                return <TextField {...props} label={_(msg`Estado actual`)} />;
              }}
            />
            <ArrowRightAlt sx={{ fill: (theme) => theme.palette.text.secondary }} />
            <Controller
              control={control}
              name="operationalState"
              render={({ field, fieldState }) => (
                <Autocomplete
                  {...field}
                  onChange={(_, value) => field.onChange(value)}
                  value={field.value ?? ''}
                  disableClearable
                  options={to(currentOperationalState)}
                  getOptionLabel={(option) => {
                    const { label } = getDeviceOperationalStateAttributes(option, i18n);
                    return label;
                  }}
                  renderInput={(props) => {
                    return (
                      <TextField
                        {...props}
                        label={_(msg`Nuevo estado`)}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    );
                  }}
                />
              )}
            />
          </Box>
        </TabContent>
        <ModalActions
          onClose={props.onClose}
          onSubmit="submit"
          dirtyForm={formState.isDirty}
          submitLoading={formState.isSubmitting}
          onResetForm={() => reset()}
        />
      </form>
    </>
  );
};

const StatusTab: FC<DeviceTabProps> = (props) => {
  return (
    <ApolloErrorBoundary variant="modal">
      <Suspense fallback={<TabFallback {...props} />}>
        <Component {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};

export default StatusTab;
